// Draws a roadmap as a standalone SVG, used for image export, printing and PDFs.
// It does not depend on the DOM, so it is easy to test and renders the same everywhere.
import { totalSlots, SLOTS_PER_SPRINT, STATUSES, dependencyIssues } from './schema';
import { phaseColour, UNASSIGNED } from './palette';
import { hasDates, sprintStartDate, sprintEndDate, formatDay, slotToDate, todaySlot, formatDateRange, rangeDates } from './dates';
import { groupRows } from './groups';

const FONT = "Inter, 'Segoe UI', Helvetica, Arial, sans-serif";

const THEMES = {
  light: { bg: '#ffffff', panel: '#f6f6fa', alt: '#f1f1f7', line: '#e8e8f0', text: '#17142a', text2: '#55566b', text3: '#8a8ba0', iris: '#6b6cf7', milestone: '#e5484d' },
  dark: { bg: '#110e1f', panel: '#17142a', alt: '#1c1930', line: '#2c2843', text: '#ecebf5', text2: '#b3b2c6', text3: '#7f7e96', iris: '#8f90ff', milestone: '#ff6b70' }
};

export function escapeXml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' })[c]);
}

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function mix(hex, withHex, amount) {
  const a = hexToRgb(hex);
  const b = hexToRgb(withHex);
  const c = a.map((v, i) => Math.round(v * amount + b[i] * (1 - amount)));
  return `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

function fit(text, maxWidth, size) {
  const s = String(text ?? '');
  const max = Math.floor(maxWidth / (size * 0.56));
  if (max <= 1) return '';
  return s.length > max ? `${s.slice(0, Math.max(1, max - 1))}…` : s;
}

export function renderRoadmapSvg(roadmap, { theme = 'light', now = new Date() } = {}) {
  const t = THEMES[theme] || THEMES.light;
  const total = totalSlots(roadmap);
  const sprints = roadmap.settings.sprintCount;
  const dated = hasDates(roadmap);
  const pad = 32;
  const labelW = 240;
  const slotW = Math.max(26, Math.min(64, Math.round(1120 / total)));
  const trackW = slotW * total;
  const width = pad * 2 + labelW + trackW;
  const rowH = 34;
  const groupH = 26;
  const phaseById = new Map(roadmap.phases.map((p) => [p.id, p]));
  const memberById = new Map(roadmap.members.map((m) => [m.id, m]));
  const issues = dependencyIssues(roadmap);
  const rows = groupRows(roadmap.projects, new Set());

  const out = [];
  let y = pad;

  // Title block
  out.push(`<text x="${pad}" y="${y + 22}" font-size="22" font-weight="700" fill="${t.text}">${escapeXml(roadmap.name)}</text>`);
  y += 34;
  const range = dated ? formatDateRange(rangeDates(roadmap.settings, 0, total)) : '';
  const sub = [`${sprints} sprint${sprints === 1 ? '' : 's'}`, `${roadmap.projects.length} project${roadmap.projects.length === 1 ? '' : 's'}`, range].filter(Boolean).join('  ·  ');
  out.push(`<text x="${pad}" y="${y + 12}" font-size="12" fill="${t.text3}">${escapeXml(sub)}</text>`);
  y += 26;

  // Legend
  let lx = pad;
  const legend = [...roadmap.phases.map((p) => ({ name: p.name, colour: phaseColour(p.color).solid }))];
  for (const s of STATUSES.filter((s) => s.value !== 'committed' && roadmap.projects.some((p) => p.status === s.value))) {
    legend.push({ name: s.label, status: s.value });
  }
  for (const item of legend) {
    if (lx > width - pad - 120) {
      lx = pad;
      y += 18;
    }
    if (item.status) {
      const dash = item.status === 'likely' ? '3 2' : '1.5 2';
      out.push(`<rect x="${lx}" y="${y + 2}" width="14" height="10" rx="3" fill="none" stroke="${t.text3}" stroke-dasharray="${dash}"/>`);
      out.push(`<text x="${lx + 20}" y="${y + 11}" font-size="11" fill="${t.text2}">${escapeXml(item.name)}</text>`);
      lx += 26 + item.name.length * 6.4 + 14;
    } else {
      out.push(`<rect x="${lx}" y="${y + 3}" width="9" height="9" rx="3" fill="${item.colour}"/>`);
      out.push(`<text x="${lx + 15}" y="${y + 11}" font-size="11" fill="${t.text2}">${escapeXml(item.name)}</text>`);
      lx += 15 + item.name.length * 6.4 + 18;
    }
  }
  if (legend.length) y += 26;

  // Sprint header
  const x0 = pad + labelW;
  const headH = dated ? 36 : 26;
  for (let n = 1; n <= sprints; n++) {
    const x = x0 + (n - 1) * SLOTS_PER_SPRINT * slotW;
    const w = SLOTS_PER_SPRINT * slotW;
    out.push(`<rect x="${x + 2}" y="${y}" width="${w - 4}" height="${headH}" rx="7" fill="${t.panel}" stroke="${t.line}"/>`);
    out.push(`<text x="${x + w / 2}" y="${y + (dated ? 15 : 17)}" font-size="11" font-weight="600" text-anchor="middle" fill="${t.text2}">${escapeXml(fit(`Sprint ${n}`, w - 6, 11))}</text>`);
    if (dated) {
      const label = `${formatDay(sprintStartDate(roadmap.settings, n))} to ${formatDay(sprintEndDate(roadmap.settings, n))}`;
      out.push(`<text x="${x + w / 2}" y="${y + 29}" font-size="9.5" text-anchor="middle" fill="${t.text3}">${escapeXml(fit(label, w - 6, 9.5))}</text>`);
    }
  }
  y += headH + 10;
  const bodyTop = y;

  // Rows
  const bodyParts = [];
  let index = 0;
  for (const row of rows) {
    if (row.type === 'group') {
      bodyParts.push(`<text x="${pad + 4}" y="${y + 17}" font-size="11" font-weight="700" fill="${t.text3}" letter-spacing="0.06em">${escapeXml(fit(row.name.toUpperCase(), labelW + trackW, 11))}</text>`);
      bodyParts.push(`<line x1="${pad}" x2="${width - pad}" y1="${y + groupH - 2}" y2="${y + groupH - 2}" stroke="${t.line}"/>`);
      y += groupH;
      continue;
    }
    const p = row.project;
    if (index % 2 === 1) bodyParts.push(`<rect x="${pad}" y="${y}" width="${labelW + trackW}" height="${rowH}" fill="${t.alt}" opacity="0.6"/>`);
    index++;
    const people = p.memberIds.map((id) => memberById.get(id)?.name).filter(Boolean);
    const flag = issues.has(p.id) ? ' ⚠' : '';
    bodyParts.push(`<text x="${pad + 8}" y="${y + (people.length ? 15 : 21)}" font-size="12.5" font-weight="600" fill="${t.text}">${escapeXml(fit(p.name, labelW - 22, 12.5))}${flag}</text>`);
    if (people.length) bodyParts.push(`<text x="${pad + 8}" y="${y + 28}" font-size="10" fill="${t.text3}">${escapeXml(fit(people.join(', '), labelW - 20, 10))}</text>`);
    for (const b of p.blocks) {
      const phase = b.phaseId ? phaseById.get(b.phaseId) : null;
      const c = phase ? phaseColour(phase.color) : UNASSIGNED;
      const bx = x0 + b.start * slotW + 2;
      const bw = b.span * slotW - 4;
      const fill = theme === 'dark' ? mix(c.solid, t.bg, 0.24) : mix(c.solid, '#ffffff', 0.14);
      const stroke = theme === 'dark' ? mix(c.solid, t.bg, 0.6) : mix(c.solid, '#ffffff', 0.4);
      const ink = theme === 'dark' ? mix(c.solid, '#ffffff', 0.45) : c.ink;
      const dash = p.status === 'likely' ? ' stroke-dasharray="5 3"' : p.status === 'exploratory' ? ' stroke-dasharray="2 3"' : '';
      const opacity = p.status === 'exploratory' ? ' opacity="0.75"' : '';
      bodyParts.push(`<g${opacity}><rect x="${bx}" y="${y + 4}" width="${bw}" height="${rowH - 8}" rx="7" fill="${fill}" stroke="${p.status ? c.solid : stroke}"${dash}/>`);
      bodyParts.push(`<rect x="${bx + 5}" y="${y + 11}" width="3" height="${rowH - 22}" rx="1.5" fill="${c.solid}"/>`);
      const text = fit((phase?.name || '').toUpperCase(), bw - 18, 10);
      if (text) bodyParts.push(`<text x="${bx + 13}" y="${y + rowH / 2 + 3.5}" font-size="10" font-weight="700" letter-spacing="0.04em" fill="${ink}">${escapeXml(text)}</text>`);
      bodyParts.push('</g>');
    }
    y += rowH;
  }
  if (!roadmap.projects.length) {
    bodyParts.push(`<text x="${x0 + 8}" y="${y + 22}" font-size="12" fill="${t.text3}">No projects yet</text>`);
    y += rowH;
  }
  const bodyBottom = y;

  // Sprint dividers sit behind the rows
  const grid = [];
  for (let n = 0; n <= sprints; n++) {
    const x = x0 + n * SLOTS_PER_SPRINT * slotW;
    grid.push(`<line x1="${x}" x2="${x}" y1="${bodyTop - 4}" y2="${bodyBottom}" stroke="${t.line}"/>`);
  }
  out.push(...grid, ...bodyParts);

  // Milestones and today line
  const marks = [];
  for (const m of roadmap.milestones || []) {
    const x = x0 + m.slot * slotW;
    const when = m.date ? formatDay(m.date) : dated ? formatDay(slotToDate(roadmap.settings, m.slot)) : '';
    marks.push(`<line x1="${x}" x2="${x}" y1="${bodyTop - 6}" y2="${bodyBottom}" stroke="${t.milestone}" stroke-width="1.5" stroke-dasharray="4 3"/>`);
    marks.push(`<path d="M${x} ${bodyTop - 12} l5 5 -5 5 -5 -5z" fill="${t.milestone}"/>`);
    marks.push(`<text x="${x + 8}" y="${bodyBottom + 14}" font-size="10" font-weight="600" fill="${t.milestone}">${escapeXml(fit([m.name, when].filter(Boolean).join(' · '), 200, 10))}</text>`);
  }
  const today = todaySlot(roadmap, total, now);
  if (today !== null) {
    const x = x0 + today * slotW;
    marks.push(`<line x1="${x}" x2="${x}" y1="${bodyTop - 6}" y2="${bodyBottom}" stroke="${t.iris}" stroke-width="2"/>`);
    marks.push(`<text x="${x}" y="${bodyTop - 9}" font-size="9" font-weight="700" text-anchor="middle" fill="${t.iris}">TODAY</text>`);
  }
  out.push(...marks);
  y = bodyBottom + ((roadmap.milestones || []).length ? 30 : 16);

  out.push(`<text x="${pad}" y="${y + 10}" font-size="10" fill="${t.text3}">Made with Minni Map · minnimap.com · ${escapeXml(formatDay(now, { year: true }))}</text>`);
  y += 18 + pad;

  const height = Math.ceil(y);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" font-family="${FONT}">` +
    `<rect width="100%" height="100%" fill="${t.bg}"/>` +
    out.join('') +
    '</svg>';
  return { svg, width, height };
}

export function svgToPngBlob(svg, width, height, scale = 2) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        const ctx = canvas.getContext('2d');
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not create image'))), 'image/png');
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not draw image'));
    };
    img.src = url;
  });
}
